import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { KanbanTaskStatus } from '../../stores/KanbanTaskStores';

export type MoveTasksModalProps = {
  open: boolean;
  statuses: KanbanTaskStatus[];
  fromStatusId: number;
  taskCount: number;
  onConfirm: (targetStatusId: number) => void | Promise<void>;
  onCancel: () => void;
};

/**
 * Opened by the column delete flow when the server answers `409 StatusNotEmpty`: the user picks
 * the column that receives the tasks, then the delete is retried with `?moveTasksTo=`.
 */
const MoveTasksModal: React.FC<MoveTasksModalProps> = ({ open, statuses, fromStatusId, taskCount, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const targets = useMemo(
    () => statuses.filter((s) => Number(s.id) !== fromStatusId).slice().sort((a, b) => a.index - b.index),
    [statuses, fromStatusId],
  );
  const [target, setTarget] = useState<number | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTarget(targets.length > 0 ? Number(targets[0].id) : undefined);
    }
  }, [open, targets]);

  const handleOk = async () => {
    if (target == null) return;
    setBusy(true);
    try {
      await onConfirm(target);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('MoveTasksTitle')}
      okText={t('MoveAndDelete')}
      cancelText={t('Cancel')}
      okButtonProps={{ danger: true, disabled: target == null, loading: busy }}
      onOk={handleOk}
      onCancel={onCancel}
      destroyOnClose
    >
      <Typography.Paragraph>{t('MoveTasksDescription', { count: taskCount })}</Typography.Paragraph>
      <Typography.Text strong>{t('MoveTasksTarget')}</Typography.Text>
      <Select
        style={{ width: '100%', marginTop: 8 }}
        value={target}
        onChange={(value: number) => setTarget(value)}
        options={targets.map((s) => ({ value: Number(s.id), label: s.name }))}
        placeholder={t('MoveTasksTarget')}
        data-testid="move-tasks-target"
      />
    </Modal>
  );
};

export default MoveTasksModal;
